// Runs the Tracker for the reader's lifetime (interface contract §4): one
// track, the PDF's file name (Tracker takes it from the URL), started once
// the document has loaded and the page is visible; paused while the page
// is hidden, resumed when it is visible again or restored from the
// back/forward cache, and stopped on pagehide.
import { effect } from "alien-signals";
import { locale, startPath, tracker } from "./app-signals.js";
import { pdfReady } from "./book-signals.js";
import { trackerEvents } from "../constants.js";

// The tracker and document URL tracking is for. `started` stays false
// while the document loaded in a hidden tab: the session starts when the
// page is first shown, so that a background tab records no heartbeats.
let running = null;

function start(current) {
  current.started = true;
  current.tracker.initTracking([{ href: current.url }], null, current.language);
  current.tracker.startTracking(current.url);
}

effect(() => {
  const trackerService = tracker();
  const url = startPath();
  const ready = pdfReady();
  const language = locale();
  if (!trackerService || !url || !ready) return;
  if (running?.tracker === trackerService && running.url === url) return;
  // A different document (or tracker) replaces the running one.
  if (running?.started) running.tracker.stopTracking(trackerEvents.Stopped);
  running = { tracker: trackerService, url, language, started: false };
  if (document.visibilityState !== "hidden") start(running);
});

function show() {
  if (!running) return;
  if (running.started) {
    running.tracker.resumeTracking(trackerEvents.Resumed);
  } else {
    start(running);
  }
}

export function handleVisibilityChange() {
  if (document.visibilityState === "hidden") {
    if (running?.started) running.tracker.pauseTracking(trackerEvents.Paused);
  } else if (document.visibilityState === "visible") {
    show();
  }
}

export function handlePageHide() {
  if (running?.started) running.tracker.stopTracking(trackerEvents.Stopped);
}

export function handlePageShow(event) {
  if (event.persisted) show();
}

document.addEventListener("visibilitychange", handleVisibilityChange);
window.addEventListener("pagehide", handlePageHide);
window.addEventListener("pageshow", handlePageShow);
